import {CommonModule} from '@angular/common';
import {Component, Inject, Input} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {dateToString, EmTranslatePipe, stringToDate, validators} from '@emma-labs/shared/angular';
import {
  calculateAgeTruncated,
  ENVIRONMENT,
  EnvironmentBroker,
  Gender,
  HUMANIA_EMMA_MIN_AGE,
  HUMANIA_EMMA_V2_ELIGIBILITY_MAX_AGE,
  Language,
  LANGUAGE_OPTIONS,
  listOfBoyFirstNames,
  listOfGirlFirstNames,
  MOCK_USER_INFO,
  Province,
  randomDate,
  randomFirstName,
  Sex,
  SEX_OPTIONS,
  SUPPORTED_PROVINCE_POSSIBILITY,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {debounceTime} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {Client} from '../../../entities/proposal-page-object/client';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {BaseSectionComponent} from '../../base-section/base-section.component';
import {TextIndicatorComponent} from '../../text-indicator/text-indicator.component';

interface ClientSectionForm {
  firstName: FormControl<string | null>;
  middleName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  birthday: FormControl<string | null>;
  sex: FormControl<Gender | null>;
  preferredLanguage: FormControl<Language | null>;
  email: FormControl<string | null>;
  phone: FormControl<string | null>;
  province: FormControl<Province | null>;
}

type ClientSectionInput = Client & {
  proposalSubmittedAt?: Date;
};

@UntilDestroy()
@Component({
  selector: 'em-client-section',
  templateUrl: './client-section.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    NgxMaskDirective,
    DropdownModule,
    InputTextModule,
    TextIndicatorComponent,
    EmTranslatePipe,
  ],
})
export class ClientSectionComponent extends BaseSectionComponent<
  FormGroup<ClientSectionForm>,
  ClientSectionInput
> {
  displayCreateButton = true;
  age?: number;
  isProposalSubmitted = false;
  readonly sexOptions = SEX_OPTIONS;
  readonly languageOptions = LANGUAGE_OPTIONS;
  readonly supportedProvincesOptions = SUPPORTED_PROVINCE_POSSIBILITY;

  @Input({required: true}) set creationMode(isInClientCreationMode: boolean) {
    this.displayCreateButton = isInClientCreationMode;

    if (!isInClientCreationMode) {
      this.form.valueChanges
        .pipe(distinctUntilChanged(), debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
        .subscribe(() => {
          this.propsChange.emit({output: this.formToOutput()});
        });
    }
  }

  @Input({required: true})
  override set props(props: ClientSectionInput | undefined) {
    super.props = props;

    this.isProposalSubmitted = !!props?.proposalSubmittedAt;
    this.age = props?.birthday ? calculateAgeTruncated(props.birthday, props.proposalSubmittedAt) : undefined;
  }

  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);

    this.form = this.createForm();
  }

  createProposal(): void {
    this.isButtonLoading = true;
    this.propsChange.emit({
      output: this.formToOutput(),
      callback: (status) => {
        if (!status) {
          this.isButtonLoading = false;
        }
      },
    });
  }

  simulateClient(): void {
    const sex = Math.random() < 0.5 ? Sex.FEMALE : Sex.MALE;

    this.form.patchValue({
      firstName: randomFirstName(sex === Sex.FEMALE ? listOfGirlFirstNames : listOfBoyFirstNames),
      middleName:
        Math.random() > 0.5
          ? randomFirstName(sex === Sex.FEMALE ? listOfGirlFirstNames : listOfBoyFirstNames)
          : undefined,
      lastName: randomFirstName(sex === Sex.FEMALE ? listOfGirlFirstNames : listOfBoyFirstNames),
      sex,
      birthday: dateToString(
        randomDate(
          new Date(new Date().setFullYear(new Date().getFullYear() - HUMANIA_EMMA_V2_ELIGIBILITY_MAX_AGE)),
          new Date(new Date().setFullYear(new Date().getFullYear() - HUMANIA_EMMA_MIN_AGE)),
        ),
      ),
      province: Math.random() < 0.5 ? Province.QC : Province.ON,
      preferredLanguage: Math.random() < 0.5 ? Language.FRENCH : Language.ENGLISH,
      phone: MOCK_USER_INFO.phone,
      email: MOCK_USER_INFO.email,
    });
  }

  onPhonePaste(event: ClipboardEvent): void {
    const pastedData = event.clipboardData?.getData('text') || '';
    const sanitized = pastedData.replace(/\D/g, '').slice(-10);

    this.form.controls.phone.setValue(sanitized);
    event.preventDefault();
  }

  override formToOutput(): Client {
    const values = this.form.getRawValue();
    return {
      ...values,
      birthday: values.birthday ? stringToDate(values.birthday) : undefined,
    } as unknown as Client;
  }

  private createForm(): FormGroup<ClientSectionForm> {
    const form = this.fb.group<ClientSectionForm>({
      firstName: this.fb.control(null, [
        Validators.required,
        Validators.maxLength(100),
        validators.noSpecialCharacter,
        validators.noNumber,
      ]),
      middleName: this.fb.control(null, [
        Validators.maxLength(100),
        validators.noSpecialCharacter,
        validators.noNumber,
      ]),
      lastName: this.fb.control(null, [
        Validators.required,
        Validators.maxLength(100),
        validators.noSpecialCharacter,
        validators.noNumber,
      ]),
      birthday: this.fb.control(null, [
        Validators.required,
        validators.date,
        validators.dateInFuture,
        validators.atLeast18YearsOld,
      ]),
      sex: this.fb.control(null, [Validators.required]),
      preferredLanguage: this.fb.control(null, [Validators.required]),
      email: this.fb.control(null, [Validators.required, validators.email]),
      phone: this.fb.control(null, [Validators.required, validators.phone]),
      province: this.fb.control(null, [Validators.required]),
    });

    const {birthday} = form.controls;
    birthday.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(
        (value) =>
          (this.age = birthday.valid && value ? calculateAgeTruncated(stringToDate(value)) : undefined),
      );

    return form;
  }
}
